import React, { useMemo } from 'react';
import { Modal, Table, Button, Tag, Typography, Input } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCardDetail } from '../../context/CardDetailContext';
import dayjs from 'dayjs';

const { Text } = Typography;

interface DueMaturityScheduleModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  schedules: any[];
}

function formatCardNumber(cardNumber?: string) {
  if (!cardNumber || cardNumber.length < 4) return cardNumber || '';
  return `***${cardNumber.slice(-4)}`;
}

const DueMaturityScheduleModal: React.FC<DueMaturityScheduleModalProps> = ({
  open,
  onClose,
  loading,
  schedules,
}) => {
  const navigate = useNavigate();
  const { setSelectedCard, setMaturityScheduleId, setMaturityYear } =
    useCardDetail();

  // Filter options
  const cardNumbers = useMemo(
    () =>
      Array.from(
        new Set(schedules.map((s) => s.creditCard?.cardNumber).filter(Boolean))
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const cardHolderNames = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.cardHolderName).filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const customerNames = useMemo(
    () =>
      Array.from(
        new Set(
          schedules.map((s) => s.creditCard?.customer?.name).filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [schedules]
  );
  const statusTextMap: Record<string, string> = {
    OVERDUE: 'Quá hạn',
    PENDING: 'Đang chờ',
  };
  const statusOptions = [
    { text: 'Đang chờ', value: 'PENDING' },
    { text: 'Quá hạn', value: 'OVERDUE' },
  ];

  const columns = [
    {
      title: 'Ngày đến hạn',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (d: string) => (
        <Tag color="red">{dayjs(d).format('DD-MM-YYYY')}</Tag>
      ),
      sorter: (a: any, b: any) =>
        dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm ngày (dd-mm-yyyy)"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        dayjs(record.dueDate).format('DD-MM-YYYY').includes(value),
    },
    {
      title: 'Số thẻ',
      dataIndex: ['creditCard', 'cardNumber'],
      key: 'cardNumber',
      filters: cardNumbers,
      onFilter: (value: any, record: any) =>
        record.creditCard?.cardNumber === value,
      render: (_: any, r: any) => formatCardNumber(r.creditCard?.cardNumber),
    },
    {
      title: 'Chủ thẻ',
      dataIndex: ['creditCard', 'cardHolderName'],
      key: 'cardHolderName',
      filters: cardHolderNames,
      onFilter: (value: any, record: any) =>
        record.creditCard?.cardHolderName === value,
      render: (_: any, r: any) => r.creditCard?.cardHolderName,
    },
    {
      title: 'Khách hàng',
      dataIndex: ['creditCard', 'customer', 'name'],
      key: 'customer',
      filters: customerNames,
      onFilter: (value: any, record: any) =>
        record.creditCard?.customer?.name === value,
      render: (_: any, r: any) => r.creditCard?.customer?.name,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      filters: statusOptions,
      onFilter: (value: any, record: any) => record.status === value,
      render: (s: string) => (
        <Tag color={s === 'OVERDUE' ? 'volcano' : 'gold'}>
          {statusTextMap[s] || s}
        </Tag>
      ),
    },
    {
      title: 'Số tiền cần nạp',
      dataIndex: 'repaymentAmount',
      key: 'repaymentAmount',
      sorter: (a: any, b: any) => a.repaymentAmount - b.repaymentAmount,
      render: (v: number) => v?.toLocaleString(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số tiền"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        record.repaymentAmount?.toString().includes(value),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm ghi chú"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.notes || '')
          .toLowerCase()
          .includes((value || '').toLowerCase()),
      render: (n: string) => (n ? <Text type="secondary">{n}</Text> : null),
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right' as const,
      width: 90,
      render: (_: any, record: any) => (
        <Button
          icon={<EyeOutlined />}
          type="link"
          onClick={() => {
            setSelectedCard(record.creditCard);
            setMaturityScheduleId(record.id);
            setMaturityYear(dayjs(record.dueDate).year());
            navigate('/card-detail');
          }}
        >
          Xem
        </Button>
      ),
    },
  ];

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ fontWeight: 600, fontSize: 18 }}>
          Kỳ đáo hạn đến hạn/overdue hôm nay
        </span>
      }
      footer={null}
      width={1100}
      bodyStyle={{ padding: 24 }}
    >
      <div
        ref={modalBodyRef}
        style={{ maxHeight: 600, overflowY: 'auto', position: 'relative' }}
      >
        <Table
          columns={columns}
          dataSource={schedules}
          loading={loading}
          rowKey={(r) => r.id}
          scroll={{ x: 1200 }}
          sticky={{ offsetHeader: 0, getContainer: () => modalBodyRef.current! }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          bordered
          size="middle"
          locale={{ emptyText: 'Không có kỳ đáo hạn nào.' }}
        />
      </div>
    </Modal>
  );
};

export default DueMaturityScheduleModal;
